<template>
	<div v-if="editing">
		<div class="alert alert-info" v-if="editing.examquestion_id">
			{{ $t('sourced-from-question-bank') }}
			<template v-if="editing.examquestionoutdated">
				<br>
				<b-icon icon="exclamation-triangle-fill" class="text-warning"></b-icon>
				{{ $t('newer-version-available') }}
				<button class="btn btn-sm btn-warning ml-2" @click="updateFromQuestionBank">{{ $t('update-to-latest-version') }}</button>
			</template>
		</div>
		<div class="input-group mb-3">
			<input type="text" class="form-control" placeholder="ID" aria-label="ID" ref="fieldid" aria-describedby="basic-addon2" :disabled="fieldIdIsLocked" v-model="editing.id">
			<div class="input-group-append">
				<button class="btn btn-outline-secondary" type="button" @click="toggleFieldIdLocked">
					<template v-if="fieldIdIsLocked">{{ $t("edit") }}</template>
					<template v-else>{{ $t("lock") }}</template>
				</button>
			</div>
		</div>

        <div class="form-group">
			<label>{{ $t('type') }}</label>
			<select class="form-control" v-model="editing.type" @change="typeChange">
				<option v-for="(option, okey) in availableTypeOptions" :key="okey" :value="option.value">
					{{ option.text }}
				</option>
			</select>
		</div>

		 <div class="form-group"  v-if="editing.type=='select' || editing.type=='grade'">
			<div class="btn-group block">

				<a
				class="btn"
				:class="{
					'btn-light': editing.selectType != 'buttons' && editing.selectType /* backwards compatibity - default to buttons */,
					'btn-success': editing.selectType == 'buttons' || !editing.selectType /* backwards compatibity - default to buttons */
				}"
				@click="editing.selectType='buttons'"
				>{{$t('buttons')}}</a>

				<a
				class="btn"
				:class="{
					'btn-light': editing.selectType != 'dropdown',
					'btn-success': editing.selectType == 'dropdown'
				}"
				@click="editing.selectType='dropdown'"
				>{{$t("dropdown")}}</a>

			</div>
		</div>

		<template v-if="!['note_plaintext', 'note_richtext', 'note_html', 'heading'].includes(editing.type)">
			<div class="form-group">
				<label>{{ $t('question') }}</label>
				<input class="form-control" v-model="editing.question" :placeholder="$t('question')" @blur="tryGenerateFieldId(editing)">
			</div>
			<div class="form-group">
				<label>{{ $t("description") }}</label>
				<vue-editor v-model="editing.description"></vue-editor>
			</div>
			<div class="form-group">
				<label>{{ $t("info") }}</label>
				<vue-editor v-model="editing.info"></vue-editor>
			</div>
		</template>

		<div class="form-group" v-if="editing.type=='select' || editing.type=='multiselect' || editing.type=='grade'">

			<label class="mr-1">{{ $t('options') }}</label>
			<a class="btn btn-light mr-1" @click="saveOptionsToLibrary(editing.options)" v-if="!editing.optionsFromConfig && editing.type!=='grade'">{{ $t('save') }}</a>
			<a class="btn btn-light mr-1" @click="loadOptionsFromLibrary" v-if="editing.type!=='grade'">{{ $t('load') }}</a>
			<a class="btn btn-light" @click="openResponseValueGroupSelectorForField" v-if="editing.type=='select' || editing.type=='grade'">{{ $t('select-option-set') }}</a>

			<div class="mt-2">
				<template v-if="editing.optionsFromConfig">
					{{ $t('use') }} <em>{{ editing.optionsFromConfig }}</em> {{ $t('config') }}. - <a href="#" @click.prevent="editing.optionsFromConfig = null">{{ $t("unlink") }}</a>
				</template>
				<template v-else>
					<OptionsEditor :options="editing.options" :hide-add-button="editing.type=='grade'"></OptionsEditor>
				</template>
			</div>
		</div>

		<div class="form-group" v-if="editing.type=='mark'">
			<label>{{ $t('mark-lower-bound') }}</label>
			<input type="number" class="form-control" v-model.number="editing.min">
			<label class="mt-2">{{ $t('mark-upper-bound') }}</label>
			<input type="number" class="form-control" v-model.number="editing.max">
		</div>

		<div class="form-group" v-if="editing.type=='polararea'">

			<label class="mr-1">{{ $t('items') }}</label>

			<div class="mt-2">
				<template v-if="editing.optionsFromConfig">
					{{ $t('use') }} <em>{{ editing.optionsFromConfig }}</em> {{ $t('config') }}. - <a href="#" @click.prevent="editing.optionsFromConfig = null">{{ $t("unlink") }}</a>
				</template>
				<template v-else>
					<DataPointEditor :datapoints="editing.data"></DataPointEditor>
				</template>
			</div>
		</div>

		<div class="form-group" v-if="editing.type=='bool'">
			<label>{{ $t('on-style') }}</label>
			<BtnOnClassPicker :option="editing"></BtnOnClassPicker>
		</div>

		<div class="form-group" v-if="editing.type=='note_plaintext'">
			<label>{{ $t('text') }}</label>
			<textarea class="form-control" v-model="editing.text"></textarea>
		</div>

		<div class="form-group" v-if="editing.type=='note_richtext'">
			<label>{{ $t('text') }}</label>
			<vue-editor v-model="editing.text"></vue-editor>
		</div>

		<div class="form-group" v-if="editing.type=='note_html'">
			<label>{{ $t('text') }}</label>
			<textarea class="form-control" v-model="editing.text"></textarea>
		</div>

		<div class="form-group" v-if="editing.type=='heading'">
			<label>{{ $t('text') }}</label>
			<input class="form-control" v-model="editing.text">
		</div>

		<div class="form-group" v-if="Object.hasOwnProperty.call(editing, 'maxWords')">
			<label>{{ $t('max-words') }}</label>
			<input type="number" class="form-control" v-model="editing.maxWords" min="0">
		</div>

		<div class="form-group" v-if="editing.type=='select'||editing.type=='grade'||editing.type=='mark'||editing.type=='text'||editing.type=='textarea'||editing.type=='number'||editing.type=='computed'">
			<label>{{ $t('label-style') }}</label>
			<div :value="editing.labelStyle" class="btn-group block">
				<a v-for="(labelStyle, lkey) in labelStyles" :key="lkey" :class="{ 'btn': true , 'btn-light': editing.labelStyle != labelStyle.value , 'btn-success': editing.labelStyle == labelStyle.value }"  @click="editing.labelStyle=labelStyle.value" :value="labelStyle.value">{{ labelStyle.text }}</a>
			</div>
		</div>

		<div v-if="['text', 'textarea', 'richtext'].includes(editing.type)">
			<div class="form-check mt-3">
				<input
				type="checkbox"
				class="form-check-input"
				id="enableCount"
				v-model="editing.enableCount"
				/>
				<label class="form-check-label" for="enableCount">
				{{ $t('add-word-char-count') }}
				</label>
			</div>
			<div v-if="editing.enableCount" class="mt-3">
				<div class="form-group">
				<label>{{ $t('count-mode') }}</label>
				<select class="form-control" v-model="editing.countMode">
					<option value="words">{{ $t('words') }}</option>
					<option value="characters">{{ $t('characters') }}</option>
				</select>
				</div>

				<div class="form-group">
				<label>{{ $t('limit') }}</label>
				<input
					type="number"
					class="form-control"
					v-model.number="editing.countLimit"
					min="1"
					placeholder="e.g. 200"
				/>
				</div>
			</div>
			<br>
		</div>
        <div>
            <div class="form-check">
				<input
                    type="checkbox"
                    class="form-check-input"
                    id="requiredfield"
                    v-model="editing.required"
			    />
				<label class="form-check-label" for="requiredField">
				    {{ $t('this-is-a-required-field') }}
				</label>
			</div>
        </div>

		<template v-if="editing.type=='computed' && ('computesettings' in editing)">
			<div class="form-group">
				<label>{{ $t('get-values-from') }}</label>
				<select class="form-control" v-model="editing.computesettings.valuesfrom" @change="typeChange">
					<option value="all">{{ $t('whole-form') }}</option>
					<option value="page">{{ $t('selected-pages') }}</option>
					<option value="fields">{{ $t('selected-fields') }}</option>
				</select>
			</div>
			<div v-if="editing.computesettings.valuesfrom == 'page'" class="form-group">
				<label>{{ $t('pages') }}</label>
				<div>
					<span
						class="btn btn-sm mr-1"
						:class="{
							'btn-success': editing.computesettings.pages.includes(index) ,
							'btn-light': !editing.computesettings.pages.includes(index)
						}"
						v-for="(page, index) in form.pages"
						:key="index"
						@click="togglePageSelect(index)"
					>
						<b-icon v-if="editing.computesettings.pages.includes(index)" icon="check-square"></b-icon>
						<b-icon v-else icon="square"></b-icon>
						<template v-if="page.title">
							{{page.title}}
						</template>
						<template v-else>
							{{ index + 1 }}
						</template>
					</span>
				</div>
			</div>
			<div v-if="editing.computesettings.valuesfrom == 'fields'" class="form-group">
				<label>{{ $t('pages') }}</label>
				<div>
					<template v-for="page in form.pages">
						<template v-for="row in page.grid">
							<template v-for="col in row">
								<template v-for="field in col.fields">
									<span
										v-if="field.type == 'number' | field.type == 'select' | field.type == 'grade' | field.type == 'mark' | field.type == 'text' | field.type == 'textarea'"
										:key="field.id"
										class="btn btn-sm mr-1"
										:class="{
											'btn-success': editing.computesettings.fields.includes(field.id) ,
											'btn-light': !editing.computesettings.fields.includes(field.id)
										}"
										@click="toggleFieldSelect(field.id)"
									>
										<b-icon v-if="editing.computesettings.fields.includes(field.id)" icon="check-square"></b-icon>
										<b-icon v-else icon="square"></b-icon>
										{{field.question}}
									</span>
								</template>
							</template>
						</template>
					</template>
				</div>
			</div>
		</template>

		<div class="form-group" v-if="'permission' in editing">
			<label>
				<template v-if="isInputField(editing)">
					{{ $t('who-can-complete-field') }}
				</template>
				<template v-else>
					{{ $t('who-can-see-field') }}
				</template>
			</label>
			<div :value="editing.permission" class="btn-group block">
				<a :class="{ 'btn': true , 'btn-light': editing.permission?.length>0 , 'btn-success': editing.permission.length===0 }" v-on:click="editing.permission=[]">{{ $t('all') }}</a>
				<a v-for="(permission, pkey) in permissions" :key="pkey" :class="{ 'btn': true , 'btn-light': !editing.permission.includes(permission.value) , 'btn-success': editing.permission.includes(permission.value) }" v-on:click="editing.permission=togglePermission(permission.value)" :value="permission.value">{{ permission.text }}</a>
			</div>
		</div>
		<template v-if="!'permission' in editing || editing.permission?.length">
			<div class="form-check">
				<label class="form-check-label" for="hideifcantcomplete">
					<input type="checkbox" class="form-check-input" id="hideifcantcomplete" v-model="editing.hideifcantcomplete">
					{{ $t('hide-from-other-users') }}
				</label>
			</div>
			<div class="form-group" v-if="editing.hideifcantcomplete">
				<div class="btn-group block">
					<a class="btn" :class="{'btn-light':editing.showaftersignoff || editing.showafterreview,'btn-success':!(editing.showaftersignoff && editing.showafterreview) }" @click="resetFields" >{{$t('always')}}</a>
					<a class="btn" :class="{'btn-light':!editing.showafterreview,'btn-success':editing.showafterreview}" @click='clickUntilRev'>{{$t('until-reviewed')}}</a><!--before we had editing.showafterreview=true using editing.showafterreview!=editing.showafterreview-->
					<a class="btn" :class="{'btn-light':!editing.showaftersignoff,'btn-success':editing.showaftersignoff}" @click="editing.showaftersignoff=!editing.showaftersignoff" >{{$t('until-signed-off')}}</a>
				</div>
			</div>
		</template>
        <!-- Conditional Visibility Section -->
        <div class="form-group border-top pt-3" v-if="isInputField(editing)">
            <div class="d-flex align-items-center">
                <h6 class="mb-0">{{ $t('conditional-visibility') }}</h6>
                <span v-if="editing.conditional" class="ml-2 badge badge-info">{{ $t('conditional') }}</span>
            </div>

            <div class="form-check mt-2">
                <input
                    type="checkbox"
                    :id="`enable-conditional-${editing.id || _uid}`"
                    :checked="!!editing.conditional"
                    @change="toggleConditional"
                    class="form-check-input"
                />
                <label class="form-check-label" :for="`enable-conditional-${editing.id || _uid}`">
                    {{ $t('show-only-if-condition-met') }}
                </label>
            </div>

            <div v-if="editing.conditional" class="mt-3 ml-4">
                <!-- Depends on field -->
                <div class="form-group">
                <label>{{ $t('depends-on-field') }}</label>
                <select v-model="editing.conditional.dependsOn" class="form-control">
                    <option value="">{{ $t('select-field') }}</option>
                    <option
                        v-for="field in otherFields"
                        :key="field.id"
                        :value="field.id"
                        :disabled="field.id === editing.id"
                    >
                    {{ field.question || field.text || field.id }}
                    </option>
                </select>
                </div>

                <!-- Operator -->
                <div class="form-group">
                <label>{{ $t('condition') }}</label>
                <select v-model="editing.conditional.operator" class="form-control">
                    <option value="equals">{{ $t('equals') }}</option>
                    <option value="notEquals">{{ $t('not-equals') }}</option>
                    <option value="truthy">{{ $t('is-filled') }}</option>
                    <option value="falsy">{{ $t('is-empty') }}</option>
                </select>
                </div>

                <!-- Value input (only for equals/notEquals) -->
                <div v-if="['equals', 'notEquals'].includes(editing.conditional.operator)" class="form-group">
                <label>{{ $t('expected-value') }}</label>
                <input
                    v-model="editing.conditional.value"
                    class="form-control"
                    :placeholder="$t('enter-value')"
                />
                </div>
            </div>
        </div>
	</div>
</template>

<script>
import { VueEditor } from "vue2-editor";
import OptionsEditor from "./OptionsEditor";
import DataPointEditor from "./DataPointEditor";
import BtnOnClassPicker from "./BtnOnClassPicker";

export default {
	components: {
		VueEditor,
		OptionsEditor,
		DataPointEditor,
		BtnOnClassPicker
	},
	name: 'FieldEditor',
	props: {
        editing: Object,
        form: Object,
        permissions: Array,
        otherFields: {
            type: Array,
            default: () => []
        }
    },
	data() {
		return {
			userWantsToEditFieldId: false,
			options: [
				{
					value: 'bool' ,
					text: this.$t('boolean')
				},
				{
					value: 'text' ,
					text: this.$t('text-single-line')
				},
				{
					value: 'textarea' ,
					text: this.$t('text-multiline')
				},
				{
					value: 'richtext',
					text: this.$t('rich-text')
				},
				{
					value: 'number' ,
					text: this.$t('number')
				},
				// todo: add option to allow select multiple
				{
					value: 'select' ,
					text: this.$t('select')
				},
				{
					value: 'grade' ,
					text: this.$t('grade')
				},
				{
					value: 'mark' ,
					text: this.$t('mark')
				},
				{
					value: 'multiselect' ,
					text: this.$t('multiselect')
				},
				{
					value: 'fileupload',
					text: this.$t('file-upload')
				},
				{
					value: 'signature',
					text: this.$t('signature')
				},
				{
					value: 'datetime',
					text: this.$t('date-time')
				},
				{
					value: 'date',
					text: this.$t('date')
				},
				{
					value: 'polararea' ,
					text: this.$t('polar-area')
				},
				{
					value: 'heading' ,
					text: this.$t('heading')
				},
				{
					value: 'note_plaintext' ,
					text: this.$t('note-plaintext')
				},
				{
					value: 'note_richtext' ,
					text: this.$t('note-richtext')
				},
				{
					value: 'note_html' ,
					text: this.$t('note-html')
				},
				{
					value: 'computed' ,
					text: this.$t('computed-value')
				}
			],
			labelStyles: [
				{
					value: 'block',
					text: this.$t('default')
				},
				{
					value: 'inline',
					text: this.$t('inline')
				},
				{
					value: 'justify',
					text: this.$t('justify')
				},
				{
					value: 'extra-space',
					text: this.$t('extra-space')
				}
			],
		}
	},
	methods: {
		togglePermission(permission) {
			const updatedPermission = [...this.editing.permission];
			const index = this.editing.permission.indexOf(permission);
			if(permission){
				if (index === -1) {
					// permission not found in the array, add it
					updatedPermission.push(permission);
				} else {
					// permission found in the array, remove it
					updatedPermission.splice(index, 1);
				}
				this.editing.permission = updatedPermission;
				if (this.editing.permission.length==this.permissions.length){
					this.editing.permission=[];
				}
			} else {
				this.editing.permission=[]
			}
			return this.editing.permission;

  		},
        toggleConditional(e) {
            if (e.target.checked) {
                this.$set(this.editing, 'conditional', {
                    dependsOn: '',
                    operator: 'equals',
                    value: ''
                });
            } else this.$delete(this.editing, 'conditional');

            this.$forceUpdate();
        },
		resetFields(){
			this.editing.showaftersignoff=false;
			this.editing.showafterreview=false;
		},
		clickUntilRev(){
			this.editing.showafterreview=!this.editing.showafterreview;
			this.editing.showaftersignoff=true;
		},
		loadOptionsFromLibrary() {

        	const data = {
        		type: 'selectoptions'
			}

 			this.$cfetch(`${this.$settings.APIBaseURL}/formlibrary/list`, {
				data
			})
			.then(json => {
				if (json.status === "SUCCESS") {

					const items = json.items;

					// add hardwired config items (specialties, clinics etc)
					items.push({
						'formlibrary_type': 'selectoptionsfromconfig',
						'formlibrary_title': 'Clinics (from config)',
						'config_name': 'clinics'
					});
					items.push({
						'formlibrary_type': 'selectoptionsfromconfig',
						'formlibrary_title': 'Specialties (from config)',
						'config_name': 'specialties'
					});

					const self = this;
					this.$store.commit("openModal", {
						type: "selector",
						title: this.$t('select-options'),
						selectCallback: function(selection) {
							if(selection.formlibrary_type == 'selectoptions') {
								self.$set(self.editing, 'options', selection.formlibrary_json);
								self.$set(self.editing, 'optionsFromConfig', null);
							} else if(selection.formlibrary_type == 'selectoptionsfromconfig') {
								self.$set(self.editing, 'options', []);
								self.$set(self.editing, 'optionsFromConfig', selection.config_name);
							}
							self.$store.commit("closeModal");
						},
						selected: [],
						data: items,
						captionKey: 'formlibrary_title',
						subcaptionKey: 'formlibrary_notes'
					});
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
			})
		},
    	getDefaultField(type = 'text') {
    		let field;
    		if(this.isInputField({type})) {
    			field = {
					question: null,
					description: null,
					info: null,
					type,
					value: null,
					permission: [],
					hideifcantcomplete: false,
					showaftersignoff: false,
					showafterreview: false,
					labelStyle: 'block',
					id: null,
					enableCount: false,
					countMode: '',
					countLimit: null,
                    required: false,
                    conditional: null
				}

				if(type == 'select' || type == 'grade') {
					// add extra properties for select fields
					field = Object.assign(field, {
						selectType: 'buttons',
						optionsFromConfig: null,
						options: [],
						// null, same as every other field type's "untouched" default. An option's
						// own value is never null (the question bank's N/A sentinel is -1, see
						// responsevaluegroup), so there's no longer a clash to avoid here - and
						// unlike undefined, null survives JSON.stringify, so the field stays
						// reactive across the save/reload round-trip (undefined gets dropped,
						// which Vue 2 then can't pick back up as a newly-added property).
						value: null
					})
				} else if(type == 'mark') {
					// numeric, bounded 0-100 by default
					field = Object.assign(field, {
						min: 0,
						max: 100
					})
				} else if(type == 'multiselect') {
					// add extra properties for select fields
					field = Object.assign(field, {
						selectType: 'buttons',
						optionsFromConfig: null,
						options: [] ,
						value: []
					});
				} else if(['textarea'/*, 'richtext'*/].includes(type)) {
					// add max words property
					field.maxWords = null;
				} else if(type == 'polararea') {
					// add extra properties for select fields
					field = Object.assign(field, {
						data: []
					})
				}


    		} else {
    			// headings, notes
    			field = {
    				type,
    				text: null,
					permission: [],
					hideifcantcomplete: false,
					showaftersignoff: false,
					showafterreview: false,
                    conditional: null
    			}
    			if(type == 'computed') {
					// add extra properties for select fields
					field = Object.assign(field, {
						question: null,
						description: null,
						labelStyle: 'block',
						computesettings: {
							valuesfrom: null ,
							pages: [] ,
							fields: [] ,
							textmapping: {}
						}
					});
				}
    		}

			return field;
    	},
    	isInputField(field) {
    		return ['text', 'textarea', 'number', 'select', 'grade', 'mark', 'multiselect', 'bool', 'fileupload', 'signature', 'richtext', 'datetime', 'date', 'polararea'].includes(field.type);
    	},
    	toggleFieldIdLocked() {
    		this.userWantsToEditFieldId = !this.userWantsToEditFieldId;
    	},
    	typeChange(e) {
    		// catch edge case where changing type from fileupload to something else
    		// fileupload val is an array which will cause errors with other fields that expect a string
    		if(e.target.value == 'multiselect'){
    			this.editing.value = [];
    		} else if(e.target.value != 'fileupload' && Array.isArray(this.editing.value)) {
    			this.editing.value = null;
    		}
    		this.patchField(this.editing);
    		if(!this.isInputField(this.editing)) {
    			this.editing.hideifcantcomplete = true;
    		}
    	},
		patchField(field) {
			const defaultField = this.getDefaultField(field.type);
			for (const [key, value] of Object.entries(defaultField)) {
				if(!Object.hasOwnProperty.call(field, key)) {
					this.$set(field, key, value);
				}
			}
			if (!Object.hasOwnProperty.call(field, 'enableCount')) {
				this.$set(field, 'enableCount', false);
			}
			if (!Object.hasOwnProperty.call(field, 'countMode')) {
				this.$set(field, 'countMode', 'words');
			}
			if (!Object.hasOwnProperty.call(field, 'countLimit')) {
				this.$set(field, 'countLimit', 100);
  			}
			// remove properties not in default field
			// for (const key of Object.keys(field)) {
			// 	if(!Object.hasOwnProperty.call(defaultField, key)) {
			// 		this.$delete(field, key);
			// 	}
			// }
			// Add some stuff to options
			if(Object.hasOwnProperty.call(field, 'options')) {
				for (const option of field.options) {
					if (!option || typeof option !== 'object') continue;
				
					if (!('info' in option)) {
						this.$set(option, 'info', null);
					}
				
					if (!('showinfo' in option)) {
						this.$set(option, 'showinfo', false);
					}
				}
			}
			//this.tryGenerateFieldId(field);
		},
    	// tryGenerateFieldId(field) {
    	// 	if(Object.hasOwnProperty.call(field, 'id') && !field.id && field.question) {
    	// 		// trim question
    	// 		const question = field.question.trim();
	    // 		// replace all except word chars and whitespace
	    // 		let id = question.replace(/[^\w\s]/g,'');
	    // 		// replace any whitepace with a hyphen
	    // 		id = id.replace(/\s+/g, '-');
	    // 		// convert to lowercase
	    // 		id = id.toLowerCase();
	    // 		field.id = id;
	    // 	}
    	// },
        tryGenerateFieldId(field) {
            if (field.id && typeof field.id === 'string' && field.id.trim() !== '') {
                return; // already valid
            }
            if (field.question) {
                let id = field.question.trim().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').toLowerCase();
                if (!id) id = 'field_' + Date.now();
                field.id = id; // string
            } else {
                field.id = 'field_' + Date.now(); // string
            }
        },
    	setUserWantsToEditField(val) {
    		this.userWantsToEditFieldId = val;
    	},
    	saveItemToLibrary(item, type, dialogTitle, firstDialogPrompt) {
			const self = this;
			this.$store.commit("openDialog" , {
				title: dialogTitle,
				prompt: firstDialogPrompt,
				confirmBtnText: this.$t('continue'),
				cancelBtn: true,
				confirmCallBack: function(title) {

					self.$nextTick(function() {
						self.$store.commit("openDialog" , {
							title: dialogTitle,
							prompt: this.$t('notes'),
							multilinePrompt: true,
							confirmBtnText: this.$t('save'),
							cancelBtn: true,
							confirmCallBack: function(notes) {

					        	const data = {
									settings: item,
									title,
									type, // tab, row, field, fieldgroup, selectoptions
									notes
								}

					 			self.$cfetch(`${self.$settings.APIBaseURL}/formlibrary/save`, {
									method: "post",
									data
								})
								.then(json => {
									if (json.status === "SUCCESS") {
										self.$store.commit("openDialog" , {
											title: self.$t('saved-to-library'),
											content: self.$t('item-successfully-saved-to-library')
										});
									}
								})
								.catch(error => {
									self.$log.error.call(self, error);
								});
							}
						});
					})

				}
			});
    	},
    	saveFieldToLibrary(field) {

    		this.saveItemToLibrary(
    			field,
    			'field',
    			this.$t('save-field-to-library'),
    			this.$t('field-name')
    		);

    	},
    	saveOptionsToLibrary(options) {

    		this.saveItemToLibrary(
    			options,
    			'selectoptions',
    			this.$t('save-options-to-library'),
    			this.$t('options-name')
    		);

    	},
    	togglePageSelect(index){
    		var item = this.editing.computesettings.pages.indexOf(index);
    		if(item != -1){
    			this.editing.computesettings.pages.splice(item , 1);
    		} else {
    			this.editing.computesettings.pages.push(index)
    		}
    	},
    	toggleFieldSelect(id){
    		var item = this.editing.computesettings.fields.indexOf(id);
    		if(item != -1){
    			this.editing.computesettings.fields.splice(item , 1);
    		} else {
    			this.editing.computesettings.fields.push(id)
    		}
    	},
    	editIdField(){
    		this.userWantsToEditFieldId = true;
    		this.$refs.fieldid.focus();
    	},
    	updateFromQuestionBank() {
    		const current = this.editing.examquestioncurrentversion;
    		if(!current) return;
    		this.$set(this.editing, 'question', current.question);
    		this.$set(this.editing, 'description', current.description);
    		this.$set(this.editing, 'info', current.info);
    		this.$set(this.editing, 'examquestionversion_id', current.examquestionversion_id);
    		if(current.examquestiontype_name === 'select') {
    			this.$set(this.editing, 'responsevaluegroup_id', current.responsevaluegroup_id);
    			this.$set(this.editing, 'options', (current.options || []).map(o => ({
    				responsevalue_id: o.responsevalue_id,
    				value: o.value,
    				caption: o.caption,
    				info: o.description,
    				cssclass: o.cssclass,
    				showinfo: false
    			})));
    		} else if(current.examquestiontype_name === 'mark') {
    			this.$set(this.editing, 'min', current.markmin);
    			this.$set(this.editing, 'max', current.markmax);
    		}
    		this.$set(this.editing, 'examquestionoutdated', false);
    	},
    	// Lets a question-bank-sourced (grade) field be repointed at a different standardised
    	// response value group, without having to go back to the Question Bank editor.
    	openResponseValueGroupSelectorForField() {
    		const self = this;
    		this.$cfetch(`${this.$settings.APIBaseURL}/responsevaluegroups/list`, {
    			data: { search: '' }
    		})
    		.then(json => {
    			if (json.status === 'SUCCESS') {
    				this.$store.commit("openModal", {
    					type: "selector",
    					title: this.$t('choose-response-value-group'),
    					selectCallback: function(selection) {
    						self.$set(self.editing, 'responsevaluegroup_id', selection.id);
    						self.$set(self.editing, 'options', (selection.values || []).map(v => ({
    							responsevalue_id: v.responsevalue_id,
    							value: v.value,
    							caption: v.caption,
    							info: v.description,
    							cssclass: v.cssclass,
    							showinfo: false
    						})));
    						self.$store.commit("closeModal");
    					},
    					selected: [],
    					data: json.results,
    					captionKey: 'title',
    					subcaptionKey: 'name',
    					idProperty: 'id'
    				});
    			}
    		})
    		.catch(error => {
    			this.$log.error.call(this, error);
    		});
    	},
    	// Saves a grade/mark field to the question bank - creating it the first time (name is
    	// auto-generated from the field id, since there's no interactive moment to prompt for one
    	// during a whole-form save) or pushing an update to the entry it's already linked to.
    	// Called automatically by Form.vue whenever the whole form is saved - there is no manual
    	// "save to question bank" step any more. form_id is passed through so the backend can tell
    	// whether this form has ever actually been responded to (see save.controller.class.php) -
    	// if not, the edit can overwrite the current version in place instead of bumping it.
    	postQuestionBankSave(field, formId, silent, nameAttempt){
    		const self = this;
    		const examquestiontype = field.type === 'grade' ? 'select' : 'mark';
    		const data = {
    			id: field.examquestion_id || undefined,
    			name: field.examquestion_id ? undefined : this.generateQuestionBankName(field, nameAttempt),
    			question: field.question,
    			description: field.description,
    			info: field.info,
    			examquestiontype,
    			form_id: formId,
    			responsevaluegroup_id: examquestiontype === 'select' ? field.responsevaluegroup_id : null,
    			markmin: examquestiontype === 'mark' ? field.min : null,
    			markmax: examquestiontype === 'mark' ? field.max : null,
    			options: examquestiontype === 'select' ? (field.options || []).map(option => ({
    				responsevalue_id: option.responsevalue_id,
    				description: option.info || null,
    				cssclass: option.cssclass || null
    			})) : []
    		};

    		return this.$cfetch(`${this.$settings.APIBaseURL}/examquestions/save`, {
    			method: "post",
    			data
    		})
    		.then(json => {
    			// Name collision on create - retry with the next incrementing suffix rather than
    			// surfacing an error, since the field's id-derived name is only ever a starting
    			// guess, not something the user chose deliberately.
    			if(json.status !== 'SUCCESS' && json.errorType === 'NAME_ALREADY_EXISTS' && !field.examquestion_id){
    				const nextAttempt = (nameAttempt || 1) + 1;
    				if(nextAttempt <= 50){
    					return self.postQuestionBankSave(field, formId, silent, nextAttempt);
    				}
    			}

    			if(json.status === 'SUCCESS') {
    				const current = json.result.currentversion;
    				self.$set(field, 'examquestion_id', json.result.id);
    				self.$set(field, 'examquestionversion_id', current ? current.examquestionversion_id : null);
    				self.$set(field, 'examquestioncurrentversion', current);
    				self.$set(field, 'examquestionoutdated', false);
    				if(examquestiontype === 'select' && current){
    					self.$set(field, 'options', (current.options || []).map(o => ({
    						responsevalue_id: o.responsevalue_id,
    						value: o.value,
    						caption: o.caption,
    						info: o.description,
    						cssclass: o.cssclass,
    						showinfo: false
    					})));
    				}
    				if(!silent){
    					self.$store.commit("openDialog", {
    						title: self.$t('saved-to-question-bank'),
    						content: self.$t('question-bank-updated')
    					});
    				}
    			} else {
    				self.$store.commit("openDialog", {
    					title: self.$t('error'),
    					content: json.errorMessage || self.$t('unexpected-error-saving')
    				});
    			}
    			return json;
    		})
    		.catch(error => {
    			self.$log.error.call(self, error);
    			self.$store.commit("openDialog", {
    				title: self.$t('error'),
    				content: self.$t('unexpected-error-saving')
    			});
    		});
    	},
    	// Bank names are stable identifiers (lowercase, no spaces) - the field's own id is already
    	// slugified this way (see tryGenerateFieldId), so reuse it rather than prompting for one.
    	// On a collision (nameAttempt > 1, see postQuestionBankSave's retry) an incrementing suffix
    	// is appended until a free name is found.
    	generateQuestionBankName(field, nameAttempt) {
    		const base = field.id ? field.id.toString().toLowerCase().replace(/[^a-z0-9_-]/g, '') : ('question' + Date.now());
    		return nameAttempt > 1 ? `${base}-${nameAttempt}` : base;
    	}
	},
	computed: {
    	fieldIdIsLocked() {
    		return this.userWantsToEditFieldId ? null : true;
    	},
    	// grade/mark are the only two question bank field types - type alone is the marker that a
    	// field belongs to the bank, synced automatically whenever the whole form is saved.
    	isQuestionBankField() {
    		return ['grade', 'mark'].includes(this.editing.type);
    	},
    	availableTypeOptions() {
    		if(this.isQuestionBankField) {
    			return this.options.filter(option => ['grade', 'mark'].includes(option.value));
    		}
    		return this.options;
    	}
	}
};
</script>
